import React, { useState, useMemo } from "react";
import { Link, useNavigate } from "@/i18n/router";
import { motion } from "framer-motion";
import { Search, ArrowRight, BookOpen, Layers, Shield, Boxes, FileCode, Rocket } from "lucide-react";
import { Seo } from "@/lib/seo";
import { PageHero } from "@/components/layout/PageHero";
import { Stagger, staggerItem } from "@/components/motion/Reveal";
import { PlatformCta } from "@/components/sections/PlatformCta";
import { DOC_NAV, DOCS, buildDocIndex } from "@/data/docs";
import { useLang } from "@/i18n/LanguageContext";
import { translateString } from "@/i18n/translate";

const CAT_ICON = {
  "Getting Started": Rocket,
  "Platform Architecture": Layers,
  "Governance Framework": Shield,
  "AI Governance OS": Boxes,
  Reference: BookOpen,
};

export default function Documentation() {
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const { lang, tr } = useLang();
  const index = useMemo(() => buildDocIndex(), []);
  const results = query.length >= 2 ? index.filter((d) => d.text.includes(query.toLowerCase())).slice(0, 8) : [];

  const faq = [
    { q: tr("Where should I start?"), a: tr("Begin with the Getting Started section — Installation and Configuration — then read the Architecture reference. Governance leads should review the Governance Framework and Compliance Mapping.") },
    { q: tr("Is the documentation versioned?"), a: tr("Yes. Use the version selector in the documentation sidebar to switch between the current release and legacy versions.") },
    { q: tr("How do I integrate governance into my pipelines?"), a: tr("See the API Documentation and API Integration Examples for authenticated REST endpoints, webhooks and official SDKs.") },
  ];

  return (
    <>
      <Seo
        title={tr("Documentation")}
        description={tr("Enterprise documentation for the AstroLife™ AI Governance OS: architecture, deployment, installation, configuration, administration, operations, API reference and governance framework.")}
        path="/documentation"
        keywords="AI governance documentation, architecture, deployment, installation, API documentation, governance framework, AstroLife"
        breadcrumbs={[{ label: tr("Documentation") }]}
        faq={faq}
      />
      <PageHero
        overline={tr("Documentation")}
        title={tr("Enterprise documentation platform.")}
        subtitle={tr("Everything engineering, governance and executive teams need to install, operate, integrate and audit verifiable AI governance at scale.")}
        breadcrumbs={[{ label: tr("Documentation") }]}
      >
        <div className="relative mt-8 max-w-2xl">
          <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-silver/40" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={tr("Search the documentation…")}
            data-testid="docs-home-search"
            className="w-full rounded-full border border-navy-border bg-navy-surface py-4 pl-12 pr-4 text-white placeholder:text-silver/40 focus:border-astro-blue focus:outline-none"
          />
          {results.length > 0 && (
            <div className="absolute z-30 mt-2 w-full overflow-hidden rounded-xl border border-navy-border bg-navy-surface shadow-2xl" data-testid="docs-home-results">
              {results.map((r) => (
                <button key={r.slug} onClick={() => navigate(`/documentation/${r.slug}`)} data-testid={`docs-home-result-${r.slug}`}
                  className="block w-full px-4 py-3 text-left hover:bg-navy transition-colors">
                  <span className="font-medium text-white">{r.title}</span>
                  <span className="block text-xs text-silver/40">{r.category} — {r.description.slice(0, 80)}…</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </PageHero>

      <section className="bg-navy py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Stagger className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {DOC_NAV.map((group) => {
              const Icon = CAT_ICON[group.category] || FileCode;
              return (
                <motion.div key={group.category} variants={staggerItem} className="rounded-2xl border border-navy-border bg-navy-surface p-7">
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-astro-blue/10 text-astro-light">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h2 className="mt-5 font-heading text-lg font-semibold text-white">{tr(group.category)}</h2>
                  <ul className="mt-4 space-y-1.5">
                    {group.items.map((slug) => (
                      <li key={slug}>
                        <Link to={`/documentation/${slug}`} data-testid={`docs-home-link-${slug}`}
                          className="group flex items-center justify-between gap-2 rounded-lg py-1.5 text-sm text-silver/70 hover:text-white transition-colors">
                          {translateString(DOCS[slug]?.title, lang)}
                          <ArrowRight className="h-3.5 w-3.5 flex-shrink-0 text-silver/30 transition-transform group-hover:translate-x-1 group-hover:text-astro-light" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </Stagger>
        </div>
      </section>
      <PlatformCta />
    </>
  );
}
